import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, map, Subject } from 'rxjs';
import { get } from 'lodash';

import { SocialMediaType, SocialType } from '@hiptraveler/data-access/api';
import { ApiService, getWindowRef, openedWindowPopUp } from '@hiptraveler/common';
import { CSRF_KEY, MOBILE_SCREEN, SOCIAL_URL_PATHS } from './common';

@Injectable({
  providedIn: 'root'
})
export class SocialApiPopupService {

  stateValue$$ = new BehaviorSubject<string | null>(null);
  stateValue$ = this.stateValue$$.asObservable().pipe(
    filter(Boolean),
    map(e => e.split('::') as [ SocialMediaType, string ])
  );

  poolingSuccess$ = new Subject<void>();
  timer$ = new Subject<void>();

  constructor(
    private apiService: ApiService
  ) { }

  openPopupWindow(social: SocialType): void {
    social === 'tk' && this.setupPopupWindowByUrl('tiktok', '/trip-planner/tk/oauth');
    social === 'tw' && this.setupPopupWindowByUrl('twitter', '/trip-planner/tw/oauth');
    social === 'fb' && this.setupPopupWindowByUrl('facebook', '/trip-planner/fb/business/auth');
    social === 'insta' && this.setupPopupWindowByUrl('instagram', '/trip-planner/insta/auth');
  }

  buildSocialMediaAuthUrl(
    social: SocialMediaType,
    csrfState: string = this.getStateBySocial(social)
  ) {

    let url = get(SOCIAL_URL_PATHS, social, SOCIAL_URL_PATHS['tiktok']);
    url = this.apiService.hosted ? this.apiService.parseUrl(url) : url;
    url += `?state=${csrfState}`;

    return { url, csrfState };
  }

  observeSocialAuth(social: SocialMediaType, csrfState: string): void {
    this.timer$.next();
    this.poolingSuccess$.next();
    this.stateValue$$.next(`${social}::${csrfState}`);
  }

  private setupPopupWindowByUrl(social: SocialMediaType, url: string): void {
    const _window = getWindowRef();
    if (!_window) return;

    const csrfState = this.getStateBySocial(social);
    url = this.buildSocialMediaAuthUrl(social, csrfState).url;

    const mobileScreen = _window.document.body.clientWidth <= MOBILE_SCREEN;
    const target = mobileScreen ? '_break' : 'popupWindow';

    _window[openedWindowPopUp] = _window.open(url, target, this.windowFeatures);

    this.observeSocialAuth(social, csrfState);
  }

  private get windowFeatures(): string {
    const _window = getWindowRef();
    if (!_window) return '';
    
    const mobileScreen = _window.document.body.clientWidth <= MOBILE_SCREEN;
    if (mobileScreen) return '';

    const { width, height } = {width: 600, height: 700 };
    const { left, top } = {
      left: (_window.screen.width - width) / 2,
      top: (_window.screen.height - height) / 2
    };
    return `width=${width},height=${height},scrollbars=yes,left=${left},top=${top}`;
  }

  private getStateBySocial(social: SocialMediaType): string {
    const _window = getWindowRef();
    const csrfState = _window.CryptoJS.lib.WordArray.random(32).toString() as string;
    const socialLinks = JSON.parse(_window.sessionStorage.getItem(CSRF_KEY) || '{}');

    if (!_window) return csrfState;

    if (socialLinks[social]) {
      return socialLinks[social];
    } else {
      socialLinks[social] = csrfState;
      _window.sessionStorage.setItem(CSRF_KEY, JSON.stringify(socialLinks));
      return socialLinks[social];
    }
  }

}
